// src/components/ShopConfig/ShopConfigList.js
import React from "react";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Edit,
  Trash2,
  CreditCard,
  ShoppingBag,
  CheckCircle,
  XCircle,
  AtSign,
  FileCode,
  Calendar,
} from "lucide-react";

function ShopConfigList({ configs, onEdit, onDelete }) {
  // If no configs, show a message
  if (!configs || configs.length === 0) {
    return (
      <div className="text-center py-16 bg-gray-50 rounded-lg border border-dashed border-gray-300">
        <ShoppingBag className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-6 text-lg font-medium text-gray-900">
          No configurations found
        </h3>
        <p className="mt-2 text-sm text-gray-500 max-w-md mx-auto">
          Get started by creating a new shop configuration. Add payment gateways
          and configure settings for your shop.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {configs.map((config) => (
        <Card
          key={config.templateName}
          className="overflow-hidden transition-all hover:shadow-md border-gray-200"
          hover
        >
          <CardContent className="p-0">
            <div className="grid grid-cols-1 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-gray-200">
              <div className="p-5 md:col-span-3">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {config.shopName}
                      </h3>
                      <Badge
                        variant={config.isActive ? "success" : "default"}
                        className={`ml-3 ${
                          config.isActive
                            ? "bg-green-100 text-green-800 border-green-200"
                            : "bg-gray-100 text-gray-800 border-gray-200"
                        }`}
                      >
                        {config.isActive ? (
                          <span className="flex items-center">
                            <CheckCircle className="h-3 w-3 mr-1" /> Active
                          </span>
                        ) : (
                          <span className="flex items-center">
                            <XCircle className="h-3 w-3 mr-1" /> Inactive
                          </span>
                        )}
                      </Badge>
                    </div>
                    <div className="flex items-center mt-1.5 text-sm text-gray-500">
                      <span className="font-medium">Template:</span>
                      <code className="ml-1.5 px-1.5 py-0.5 bg-gray-100 rounded text-gray-800 font-mono text-xs">
                        {config.templateName}
                      </code>
                    </div>
                  </div>
                </div>

                <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 flex items-center mb-2">
                        <CreditCard className="h-4 w-4 mr-2 text-gray-500" />
                        Payment Gateway
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        <Badge
                          variant="primary"
                          className="rounded-md border-indigo-200 text-indigo-700 bg-indigo-50"
                        >
                          {config.paymentGateway}
                        </Badge>
                        {config.paymentGateway === "EASEBUZZ" &&
                          config.paymentMode && (
                            <Badge
                              variant="secondary"
                              className="rounded-md border-violet-200 text-violet-700 bg-violet-50"
                            >
                              {config.paymentMode === "test"
                                ? "Test Mode"
                                : "Production Mode"}
                            </Badge>
                          )}
                      </div>
                    </div>

                    {/* Strategy Information */}
                    {config.strategyInfo && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 flex items-center mb-2">
                          <FileCode className="h-4 w-4 mr-2 text-gray-500" />
                          Strategy
                        </h4>
                        <div className="flex flex-col">
                          <Badge
                            variant="secondary"
                            className="rounded-md border-blue-200 text-blue-700 bg-blue-50 inline-flex items-center justify-center py-1 mb-1"
                          >
                            <span className="text-xs truncate max-w-[220px]">
                              {config.strategyInfo.className}
                            </span>
                          </Badge>
                          <div className="flex items-center text-xs text-gray-500 mt-1">
                            <Calendar className="h-3 w-3 mr-1" />
                            <span>
                              Updated:{" "}
                              {new Date(
                                config.strategyInfo.uploadDate
                              ).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {config.merchantEmail && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 flex items-center mb-2">
                          <AtSign className="h-4 w-4 mr-2 text-gray-500" />
                          Merchant Email
                        </h4>
                        <p className="text-sm break-all text-gray-600">
                          {config.merchantEmail}
                        </p>
                      </div>
                    )}
                  </div>

                  <div>
                    {config.paymentGateway === "CCAVENUE" && (
                      <div className="bg-gray-50 rounded-md p-3 border border-gray-100">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">
                          CCAvenue Details
                        </h4>
                        <div className="space-y-2">
                          <div className="flex items-center text-sm">
                            <span className="font-medium text-gray-600 w-28">
                              Merchant ID:
                            </span>
                            <span className="text-gray-700">
                              {config.ccavenueMerchantId || "N/A"}
                            </span>
                          </div>
                          {config.ccavenueSubAccounts &&
                            config.ccavenueSubAccounts.subAccountIds && (
                              <div className="flex items-center text-sm">
                                <span className="font-medium text-gray-600 w-28">
                                  Sub-accounts:
                                </span>
                                <Badge
                                  variant="primary"
                                  className="bg-indigo-50 text-indigo-700"
                                >
                                  {
                                    Object.keys(
                                      config.ccavenueSubAccounts.subAccountIds
                                    ).length
                                  }
                                </Badge>
                              </div>
                            )}
                        </div>
                      </div>
                    )}

                    {config.paymentGateway === "EASEBUZZ" && (
                      <div className="bg-gray-50 rounded-md p-3 border border-gray-100">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">
                          Easebuzz Details
                        </h4>
                        <div className="space-y-2">
                          <div className="flex items-center text-sm">
                            <span className="font-medium text-gray-600 w-28">
                              Key:
                            </span>
                            <span className="text-gray-700">
                              {config.easebuzzKey
                                ? "••••••" + config.easebuzzKey.slice(-4)
                                : "N/A"}
                            </span>
                          </div>
                          <div className="flex items-center text-sm">
                            <span className="font-medium text-gray-600 w-28">
                              URL:
                            </span>
                            <span className="text-gray-700 truncate">
                              {config.easebuzzUrl || "N/A"}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {config.paymentGateway === "OMNIWARE" && (
                      <div className="bg-gray-50 rounded-md p-3 border border-gray-100">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">
                          Omniware Details
                        </h4>
                        <div className="space-y-2">
                          <div className="flex items-center text-sm">
                            <span className="font-medium text-gray-600 w-28">
                              Merchant ID:
                            </span>
                            <span className="text-gray-700">
                              {config.omniwareMerchantId || "N/A"}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="p-5 flex md:flex-col justify-end items-center space-x-2 md:space-x-0 md:space-y-3">
                <Button
                  onClick={() => onEdit(config)}
                  variant="outline"
                  className="flex items-center border-blue-300 hover:bg-blue-50 hover:text-blue-700 w-full justify-center"
                >
                  <Edit className="w-4 h-4 mr-2 text-blue-600" />
                  Edit
                </Button>
                <Button
                  onClick={() => onDelete(config)}
                  variant="outline"
                  className="flex items-center border-red-300 hover:bg-red-50 text-red-600 hover:text-red-700 w-full justify-center"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default ShopConfigList;
